/**
 * Client-side image compression for uploads (Spec 030, #119).
 *
 * Resizes oversized photos (e.g. phone-camera JPEGs) to a max ~2000px longest edge
 * before upload, using the native Canvas API — no new dependency (E2). Only `image/*`
 * files are touched (E3); everything else, and any image that doesn't actually shrink
 * or fails to decode/encode, passes through as the original file (E4/E5/E6).
 */

const MAX_EDGE_PX = 2000;
const JPEG_QUALITY = 0.8;

function resizedDimensions(width: number, height: number): { width: number; height: number } {
  const longestEdge = Math.max(width, height);
  if (longestEdge <= MAX_EDGE_PX) {
    return { width, height };
  }
  const scale = MAX_EDGE_PX / longestEdge;
  return { width: Math.round(width * scale), height: Math.round(height * scale) };
}

async function encodeToJpeg(canvas: HTMLCanvasElement): Promise<Blob | null> {
  return new Promise((resolve) => {
    canvas.toBlob((blob) => resolve(blob), 'image/jpeg', JPEG_QUALITY);
  });
}

function withJpegExtension(fileName: string): string {
  const dotIndex = fileName.lastIndexOf('.');
  const base = dotIndex > 0 ? fileName.slice(0, dotIndex) : fileName;
  return `${base}.jpg`;
}

export async function compressImage(file: File): Promise<File> {
  if (!file.type.startsWith('image/')) {
    return file;
  }

  try {
    const bitmap = await createImageBitmap(file);
    const { width, height } = resizedDimensions(bitmap.width, bitmap.height);

    if (width === bitmap.width && height === bitmap.height) {
      // Already within target size (E4) — nothing to do.
      bitmap.close?.();
      return file;
    }

    const canvas = document.createElement('canvas');
    canvas.width = width;
    canvas.height = height;
    const ctx = canvas.getContext('2d');
    if (!ctx) {
      bitmap.close?.();
      return file;
    }
    ctx.drawImage(bitmap, 0, 0, width, height);
    bitmap.close?.();

    const blob = await encodeToJpeg(canvas);
    if (!blob || blob.size >= file.size) {
      // Compression didn't help (E4) — keep the original.
      return file;
    }

    // Bytes are now JPEG-encoded — reflect that in the extension, otherwise e.g. a
    // re-encoded "photo.png" would misleadingly keep its .png name (L3 review finding).
    return new File([blob], withJpegExtension(file.name), { type: 'image/jpeg' });
  } catch {
    // Decode/encode failure — fall back to the original, never abort the upload (E6).
    return file;
  }
}
